import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { useLoroSelector } from "../../sync/hooks";
import type { BlockComponentProps } from "../registry";
import { LoroTextInput, bodyMap } from "../textField";
import { embedUrl, parseVideoUrl, type Provider } from "./provider";
import { useT } from "../../i18n";

interface Body {
  provider: Provider;
  videoId: string | null;
  url: string;
  startSeconds: number | null;
}

/** External video block (spec §7.4.6, §8.8): YouTube/Vimeo iframes, oEmbed card for generic URLs. */
export function ExternalVideoBlock({ handle, node, readOnly, onFocus }: BlockComponentProps) {
  const t = useT();
  const body = bodyMap(node);
  const v = useLoroSelector(
    handle.doc,
    (): Body => ({ provider: (body?.get("provider") as Provider) ?? "generic", videoId: (body?.get("videoId") as string | null) ?? null, url: String(body?.get("url") ?? ""), startSeconds: (body?.get("startSeconds") as number | null) ?? null }),
    (a, b) => a.provider === b.provider && a.videoId === b.videoId && a.url === b.url && a.startSeconds === b.startSeconds,
  );
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [play, setPlay] = useState(false);
  const [cardFor, setCardFor] = useState<{ url: string; data: { title?: string; provider_name?: string; thumbnail_url?: string } } | null>(null);
  const provider = v?.provider;
  const url = v?.url;

  useEffect(() => {
    if (provider !== "generic" || !url) return;
    let stop = false;
    void api.GET("/api/media/oembed", { params: { query: { url } } }).then((r) => {
      if (!stop && r.response.ok && r.data) setCardFor({ url, data: (r.data as { data: NonNullable<typeof cardFor>["data"] }).data });
    });
    return () => {
      stop = true;
    };
  }, [provider, url]);
  const card = cardFor && cardFor.url === url ? cardFor.data : null;

  if (!body || !v) return null;
  const commit = (raw: string) => {
    const p = parseVideoUrl(raw);
    if (!p) {
      setError(t("video.badUrl"));
      return;
    }
    // spec §7.4.6: provider and url are watched LWW keys; written together in one commit
    body.set("provider", p.provider);
    body.set("videoId", p.videoId);
    body.set("url", p.url);
    body.set("startSeconds", p.startSeconds);
    handle.doc.commit();
    setError(null);
    setDraft(null);
  };
  const embed = embedUrl(v);
  return (
    <div className="rounded-md border border-neutral-200 p-2.5 dark:border-neutral-800" data-testid="external-video-block" data-fill-column onFocus={onFocus}>
      <input
        className="w-full bg-transparent text-sm outline-none"
        placeholder={t("video.placeholder")}
        value={draft ?? v.url}
        readOnly={readOnly}
        data-testid="external-video-url"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => draft !== null && draft !== v.url && commit(draft)}
        onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      />
      {error && <div className="text-xs text-red-600">{error}</div>}
      {embed && (
        // [M21] the box the player is contained in when the block has a height (§8.6 item 8): the
        // player keeps its 16:9 and grows with the block until it is as wide as the block
        <div className="mt-2" data-fill-surface data-fill-frame>
          {play ? (
            <iframe title={t("video.frame")} src={embed} data-fill-ratio className="aspect-video w-full max-w-2xl rounded" allow="accelerometer; encrypted-media; picture-in-picture" allowFullScreen sandbox="allow-scripts allow-same-origin allow-presentation allow-popups" />
          ) : (
            <button className="flex aspect-video w-full max-w-2xl items-center justify-center rounded bg-neutral-200 text-sm dark:bg-neutral-800" data-fill-ratio onClick={() => setPlay(true)} data-testid="external-video-play">
              {t("video.load", { provider: v.provider, id: v.videoId ?? "" })}
            </button>
          )}
        </div>
      )}
      {v.provider === "generic" && v.url && (
        <a href={v.url} target="_blank" rel="noopener noreferrer" className="mt-2 block rounded-md border border-neutral-200 bg-[var(--surface)] p-2.5 text-sm dark:border-neutral-800" data-testid="external-video-card">
          {card?.thumbnail_url && <img src={card.thumbnail_url} alt="" className="mb-1 max-h-40 rounded" />}
          <div className="font-medium">{card?.title ?? v.url}</div>
          {card?.provider_name && <div className="text-xs text-neutral-500">{card.provider_name}</div>}
        </a>
      )}
      <LoroTextInput handle={handle} map={body} field="caption" placeholder={t("media.caption")} readOnly={readOnly} testId="media-caption" />
    </div>
  );
}
